import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Col, Row } from 'reactstrap';
import { useDispatch, useSelector } from 'react-redux';
import { cloneDeep } from 'lodash';
import * as Yup from 'yup';
import { acceptByBuyer, rejectByBuyer } from '../../myvehicles/MyVehiclesActions';
import { AuctionStatus } from '../../constants/AuctionStatus';
import CommonButton from './CommonButton';
import { CommonButtonVariants } from '../../constants/CommonButtonVariants';
import { fetchOrganizationDetails } from '../../settings/SettingsActions';
import { preparePriceFormat } from '../helpers/preparePriceFormat';
import { prepareBidsData } from '../helpers/prepareBidsData';
import { placeBid } from '../../search/SearchActions';
import { isDateBefore } from '../utils/date/Date.utils';
import {
  AUCTION_BID_MAX_BIDS_LIMIT,
  AUCTION_BID_MINIMUM_AMOUNT_IN_PENCE,
  AUCTION_MINIMUM_START_BID_AMOUNT_IN_PENCE,
} from '../../constants/AuctionBid';
import { ordinalSuffix } from '../helpers/ordinalSuffix';
import { validateBidAmount } from '../helpers/AuctionBidHelper';
import { pencesToPounds } from '../helpers/pencesToPounds';
import useDefaultBidLocationId from '../hooks/useDefaultBidLocationId';
import checkWhiteIcon from '../../assets/img/icons/check-white.svg';
import closeWhiteIcon from '../../assets/img/icons/close-white.svg';

function AuctionDetailsBuyerSection({ item, refreshItem, bids, status, changeStatus }) {
  const bidFormRef = useRef(null);
  const dispatch = useDispatch();
  const organizationLocationId = useDefaultBidLocationId();
  const [validationRequiredBid, setValidationRequiredBid] = useState(null);
  const [showConfirm, setShowConfirm] = useState(false);
  const [bidToSend, setBidToSend] = useState(0);
  const organizationId = useSelector((state) => state.auth.get('organizationId'));
  const bidErrorMessage = useSelector((state) => state.search.get('bidErrorMessage'));
  const isLoading = useSelector((state) => state.navbarLoader.get('isLoading'));
  const sellerOrganizationId = item?.seller?.organizationContact?.id;
  const [bidsNotifications, setBidsNotifications] = useState({});
  const bidsData = bids && bids.length > 0 ? bids : item?.bids;
  const [bidsLeft, setBidsLeft] = useState(AUCTION_BID_MAX_BIDS_LIMIT - bidsData.length);

  useEffect(() => {
    dispatch(fetchOrganizationDetails());
  }, [dispatch]);

  useEffect(() => {
    if (status) {
      changeStatus(false);
      refreshItem();
      setBidsNotifications({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  const { bidMyOrganizationIsHighest, highestBidMyOrganization, bidsMyOrganization } =
    prepareBidsData(bidsData, organizationId);

  const handleAcceptAuction = () => {
    dispatch(
      acceptByBuyer(item, () => {
        refreshItem();
      }),
    );
  };

  const handleRejectAuction = () => {
    dispatch(
      rejectByBuyer(item, () => {
        refreshItem();
      }),
    );
  };
  const renderBidders = () =>
    bidsMyOrganization &&
    bidsMyOrganization.length &&
    bidsMyOrganization
      .filter((item) => item.bidderOrganizationId === organizationId)
      .sort((a, b) => a.amountInPence - b.amountInPence)
      .map(({ bidderFirstName, bidderLastName, id, amountInPence }, index) => {
        const bidderFullName =
          bidderFirstName && bidderLastName ? `${bidderFirstName} ${bidderLastName}` : '-';
        const preparedBidder = `${ordinalSuffix(
          index + 1,
        )} bid: ${bidderFullName} ${preparePriceFormat(amountInPence, true)}`;
        return (
          <li key={id}>
            <i className="fas fa-angle-right auction-detail-buyer__arrow-icon" />
            {preparedBidder}
          </li>
        );
      });

  const addNotification = (id, message, className) => {
    const notifications = cloneDeep(bidsNotifications);
    notifications[id] = {
      className,
      message,
    };
    setBidsNotifications(notifications);
  };

  const handleBid = async () => {
    await dispatch(
      placeBid(
        item?.id,
        bidToSend * 100,
        (response) => {
          const { success, auctionId, result } = response;
          setBidsLeft(result?.bidsLeft);
          if (!success) {
            addNotification(auctionId, result, AuctionStatus.PUBLISHED);
          } else {
            let message;
            let color;

            const bidAmount = `£${bidToSend.toLocaleString('en-GB')}`;

            if (result.isHighestBid) {
              if (result.proxyBidInPence) {
                message = `Your bid of ${result.proxyBidInPence} is the highest. Your maximum bid is ${bidAmount}`;
              } else {
                message = `Your bid of ${bidAmount} is the highest`;
              }
              color = AuctionStatus.PUBLISHED;
            } else {
              message = `Your bid of ${bidAmount} is not the highest - try again`;
              color = AuctionStatus.REJECTED;
            }
            addNotification(auctionId, message, color);
          }
        },
        organizationLocationId,
      ),
    );
    setShowConfirm(false);
    if (bidsLeft !== 1) {
      bidFormRef.current.resetForm();
    }
    bidFormRef.current.setSubmitting(false);
  };

  const handleSubmit = async ({ bid }) => {
    setBidToSend(bid);
    setShowConfirm(true);
  };

  const renderBidMessage = () => {
    let message;
    let statusColor;
    if (bidMyOrganizationIsHighest) {
      if (item.proxyBidInPence) {
        message = `Your bid of ${preparePriceFormat(
          item.proxyBidInPence,
          true,
        )} is the highest. Your maximum bid is ${preparePriceFormat(
          highestBidMyOrganization?.amountInPence,
          true,
        )}`;
      } else {
        message = `Your bid of ${preparePriceFormat(
          highestBidMyOrganization?.amountInPence,
          true,
        )} is the highest`;
      }
      statusColor = AuctionStatus.PUBLISHED;
    } else if (bidsMyOrganization?.length === AUCTION_BID_MAX_BIDS_LIMIT) {
      message = `Your bid of ${preparePriceFormat(
        highestBidMyOrganization?.amountInPence,
        true,
      )} was not the highest`;
      statusColor = AuctionStatus.REJECTED;
    } else {
      message = `Your bid of ${preparePriceFormat(
        highestBidMyOrganization?.amountInPence,
        true,
      )} is not the highest - try again`;
      statusColor = AuctionStatus.REJECTED;
    }
    return (
      <CommonButton
        label={message}
        variant={CommonButtonVariants.AUCTION}
        color={statusColor}
      />
    );
  };

  const validationSchema = Yup.object().shape({
    bid: Yup.number()
      .required('Amount value is required')
      .integer('Bid must be an integer')
      .test(
        'required bid amount',
        `Your bid must be greater than or equal to ${preparePriceFormat(
          validationRequiredBid,
        )}`,
        (value) =>
          validateBidAmount(value, item, highestBidMyOrganization, setValidationRequiredBid),
      ),
  });

  const highestBid = item?.bids?.find((item) => item.isHighest);

  const getInitBidValue = useCallback(() => {
    const isCurrentBidderWinning = item.bids.some((it) => it.isHighest);
    if (item?.proxyBidInPence && !isCurrentBidderWinning) {
      // eslint-disable-next-line no-unsafe-optional-chaining
      return item?.proxyBidInPence / 100 + pencesToPounds(AUCTION_BID_MINIMUM_AMOUNT_IN_PENCE);
    }
    if (item?.highestBidInPence) {
      return (
        // eslint-disable-next-line no-unsafe-optional-chaining
        item?.highestBidInPence / 100 + pencesToPounds(AUCTION_BID_MINIMUM_AMOUNT_IN_PENCE)
      );
    }
    if (item?.minimumPriceInPence) {
      return pencesToPounds(item?.minimumPriceInPence);
    }
    return pencesToPounds(AUCTION_MINIMUM_START_BID_AMOUNT_IN_PENCE);
  }, [item]);

  const bidInitValue = getInitBidValue();

  return (
    // eslint-disable-next-line react/jsx-no-useless-fragment
    <>
      {!isDateBefore(item?.endsOn, new Date()) ||
      item.bids.some((item) => item.bidderOrganizationId === organizationId) ? (
        <>
          {(() => {
            switch (item.status) {
              case AuctionStatus.WAITING:
                return (
                  // eslint-disable-next-line react/jsx-no-useless-fragment
                  <>
                    {item?.isBuyerAcceptanceAllowed && (
                      <Row className="auction-detail-buyer-wrapper auction-detail-seller-to-accept">
                        <Col xs={6} className="mb-5">
                          <CommonButton
                            className="basic-data__button w-100"
                            label="Accept"
                            variant={CommonButtonVariants.PRIMARY}
                            handleClick={handleAcceptAuction}
                            icon={checkWhiteIcon}
                            iconAlt="Check icon"
                            iconLeft
                          />
                        </Col>
                        <Col xs={6}>
                          <CommonButton
                            className="basic-data__button red-button w-100"
                            label="Reject"
                            handleClick={handleRejectAuction}
                            variant={CommonButtonVariants.RED}
                            icon={closeWhiteIcon}
                            iconAlt="Close icon"
                            iconLeft
                          />
                        </Col>
                      </Row>
                    )}
                  </>
                );

              default:
                return null;
            }
          })()}
        </>
      ) : null}
    </>
  );
}

AuctionDetailsBuyerSection.defaultProps = {
  bids: [],
};

AuctionDetailsBuyerSection.propTypes = {
  item: PropTypes.object.isRequired,
  refreshItem: PropTypes.func.isRequired,
  status: PropTypes.bool.isRequired,
  changeStatus: PropTypes.func.isRequired,
  bids: PropTypes.array,
};

export default AuctionDetailsBuyerSection;
